/**
 * normalizeForCompare — the ONE value rule the field editor's changed-check
 * applies to BOTH sides: the rep's draft and the stored value.
 *
 * Why both sides. The editor shows the stored value raw and cleans what it
 * commits (trim, '' → null, numbers coerced). Comparing the cleaned draft
 * against the RAW stored value made every non-canonical stored value — a
 * stored '', a number in a text field, padded text, a numeric string in a
 * number field — read as an edit, so opening and closing a record dirtied it
 * with nothing typed (_AUDIT_DIRTY_ON_CLOSE_MAPPING_20261016.md). Running both
 * sides through this one function is what makes an untouched field equal to
 * itself, whatever its stored shape.
 *
 *   null / undefined   -> null   FIRST, before any String(): String(null) is
 *                                'null', which would make every EMPTY field
 *                                read as changed — a far larger regression
 *                                than the bug this fixes.
 *   String(raw).trim() === ''   -> null
 *   kind 'number'      -> Number(s), or s itself when Number(s) is NaN
 *   otherwise          -> s
 *
 * The output is exactly what the editor commits for a real edit, so a change
 * a rep types is written the same way it always was.
 *
 * ⚠ DO NOT MERGE THIS INTO fieldValueRules.js. The two disagree on purpose.
 * fieldValueRules.isEmpty treats a whitespace-only string as NOT empty, so
 * Sort and Filter give one answer about a blank-looking cell. This rule
 * treats whitespace-only as empty (null), because the editor commits it as
 * null and an untouched field must compare equal to what it would commit.
 * One "empty" for the grid, another for the editor's changed-check: folding
 * them into one module invites someone to unify them, and either direction
 * of that unification reintroduces a bug.
 *
 * Accepted, deliberate consequence (pinned in
 * FieldRecordModal.closeCommit.test.js, 1d): an edit that changes ONLY the
 * representation of a non-canonical stored value — retyping '012' as 12 in a
 * number field, or deleting only the padding around text — reads as
 * unchanged and is not written. Prod held zero such stored values on
 * 2026-10-16.
 *
 * PURE. No React, no Dexie, no storage.
 */
export function normalizeForCompare(raw, kind) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (kind === 'number') {
    const n = Number(s);
    return Number.isNaN(n) ? s : n;
  }
  return s;
}
